(ns app.domain.inspection.model
  (:require #?(:clj [datomic.api :as d])
            [app.domain.application.guards.utils :as guard-utils]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.file.model :as file-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [schema.core :as s]))

(def ^:const comply-by-email-default-days 3)

(def qualification-kinds [:qualification.type/ON-building-services
                          :qualification.type/ON-building-structural
                          :qualification.type/ON-complex-buildings
                          :qualification.type/ON-detection-lighting-power
                          :qualification.type/ON-fire-protection
                          :qualification.type/ON-hvac-house
                          :qualification.type/ON-house
                          :qualification.type/ON-large-buildings
                          :qualification.type/ON-on-site-sewage-systems
                          :qualification.type/ON-plumbing-all-buildings
                          :qualification.type/ON-plumbing-house
                          :qualification.type/ON-small-buildings])

(def QualificationKind (apply s/enum
                              qualification-kinds))

(defn inspector-group?
  "Predicate to check that the given entity is an inspector group"
  [group]
  (and (group-model/is-group? group)
       (group-model/has-role? :group.role/inspector group)))

(defn all-inspector-groups [authority-group]
  (let [;; first level child groups via index
        child-groups (group-model/child-groups authority-group)]
    (->> (concat
           child-groups
           (mapcat (partial eu/descendants :entity.type/group) child-groups))
         (filter inspector-group?))))

(defn inspector-group-not-qualification?
  "Predicate to check that the given entity is a 'true' inspector, not just a qualification."
  [group]
  (and (inspector-group? group)
       (-> group :group/qualification nil?)))

(defn inspector-qualifications
  "Given inspector groups, return a set of the configurable inspector qualifications defined by them."
  [inspector-groups]
  (->> inspector-groups
       (map :group/inspector-qualification)
       (filter some?)
       set))

(def template-kind-editable-keys
  #{:inspection.template.kind/names
    :inspection.template.kind/descriptions
    :inspection.template.kind/external-code
    :inspection.template.kind/phases
    :inspection.template.kind/visible-to-applicant?
    :domain/state
    :inspection.template.kind/visit-type
    :inspection.template.kind/subtype
    :inspection.template.kind/recurring?
    :inspection.template.kind/recurrence-interval
    :inspection.template.kind/triggers-refunds?
    :inspection.template.kind/prevent-request-when-unpaid?
    :inspection.template.kind/visit-completion-notifies?
    :inspection.template.kind/end-visit-notification-recipients
    :inspection.template.kind/end-visit-notification-for-results
    :inspection.template.kind/inspector-qualifications
    :inspection.template.kind/first-confirmation-notifies?
    :inspection.template.kind/any-confirmation-notifies?
    :inspection.template.kind/confirm-notification-recipients
    :inspection.template.kind/multiple-applicant-bookings?})


(def inspection-template-kind-phases
  #{:inspection.template.kind.phase/pre-issuance
    :inspection.template.kind.phase/post-issuance})

(def default-inspection-template-kind-phases
  #{:inspection.template.kind.phase/post-issuance})

(def phase->group-config-attr
  {:inspection.template.kind.phase/pre-issuance  :group.config/application-inspection-templates
   :inspection.template.kind.phase/post-issuance :group.config/inspection-templates})

(def group-config-attr->phase
  (set/map-invert phase->group-config-attr))

(def entity-type->inspection-phase
  {:entity.type/pre-consultation :inspection.template.kind.phase/pre-issuance
   :entity.type/application      :inspection.template.kind.phase/pre-issuance
   :entity.type/execution        :inspection.template.kind.phase/post-issuance})

(defn inspection-template-kind-phases-or-default [template-kind]
  (or (seq (:inspection.template.kind/phases template-kind))
      default-inspection-template-kind-phases))

(defn inspection-template-kind-applicable-to-phase? [phase template-kind]
  (contains? (set (inspection-template-kind-phases-or-default template-kind)) phase))

(defn inspection-template-kind-applicable-to-config-attr? [attr template-kind]
  (inspection-template-kind-applicable-to-phase? (group-config-attr->phase attr) template-kind))

(defn target-inspection-phase [target]
  (entity-type->inspection-phase (:entity/type target)))

(defn inspection-template-kind-applicable-to-target? [target template-kind]
  (if-let [phase (target-inspection-phase target)]
    (inspection-template-kind-applicable-to-phase? phase template-kind)
    true))

(defn template-kind-visible-to-applicant? [template-kind]
  (boolean (:inspection.template.kind/visible-to-applicant? template-kind)))

(def order-phase-target-entity-types
  #{:entity.type/pre-consultation
    :entity.type/application
    :entity.type/execution})

(defn order-phase-target? [entity]
  (contains? order-phase-target-entity-types (:entity/type entity)))

(defn order-phase-target->workspace [phase-target]
  ;; The workspace must be resolved reliably on the backend, so fail loudly there on an
  ;; unexpected target. On the frontend the check is skipped (best-effort display only).
  #?(:clj (when-not (order-phase-target? phase-target)
            (throw (ex-info "Not a valid order phase target"
                            {:entity/type (:entity/type phase-target)}))))
  (:entity/parent phase-target))

(defn order-template-phases-or-default [template]
  (or (seq (:order-template/phases template))
      default-inspection-template-kind-phases))

(defn order-template-applicable-to-phase? [phase template]
  (contains? (set (order-template-phases-or-default template)) phase))

(defn order-template-applicable-to-target? [target template]
  (if-let [phase (target-inspection-phase target)]
    (order-template-applicable-to-phase? phase template)
    true))


(def template-editable-keys
  #{:inspection.template/qualification
    :inspection.template/duration-minutes
    :domain/state})

(def checklist-template-editable-keys
  #{:inspection.checklist/names
    :inspection.checklist/result-set
    :inspection.checklist/custom-result-set
    :inspection.checklist/positive-result-remark?
    :inspection.checklist/negative-result-deficiency?})

(def checklist-item-group-editable-keys
  #{:inspection.checklist-item.group/names})

(def checklist-item-template-editable-keys
  #{:inspection.checklist-item/code
    :inspection.checklist-item/descriptions})

(def checklist-item-instance-editable-keys
  #{:inspection.checklist-item/note})

(def checklist-conflict-resolution-strategies
  "Available strategies for resolving checklist name conflicts during import/copy:
   - :allow-duplicates - Import/copy checklists with duplicate names as-is
   - :append-suffix - Add version suffix (v2, v3, etc.) to new checklists with duplicate names
   - :overwrite - Replace existing checklists that have the same names"
  #{:allow-duplicates :append-suffix :overwrite})

(def inspection-pdf-snippet-fragments
  {:inspection.pdf-template.type/general-report          [:inspection.pdf-template/empty-checklist-table-snippet
                                                          :inspection.pdf-template/table-for-checklists-snippet
                                                          :inspection.pdf-template/checklist-snippet
                                                          :inspection.pdf-template/checklist-group-snippet
                                                          :inspection.pdf-template/checklist-item-snippet
                                                          :inspection.pdf-template/empty-condition-table-snippet
                                                          :inspection.pdf-template/condition-table-snippet
                                                          :inspection.pdf-template/condition-snippet
                                                          :inspection.pdf-template/table-for-photos-snippet
                                                          :inspection.pdf-template/photo-snippet]
   :inspection.pdf-template.type/code-enforcement-report [:inspection.pdf-template/empty-checklist-table-snippet
                                                          :inspection.pdf-template/table-for-checklists-snippet
                                                          :inspection.pdf-template/checklist-snippet
                                                          :inspection.pdf-template/checklist-group-snippet
                                                          :inspection.pdf-template/checklist-item-snippet
                                                          :inspection.pdf-template/table-for-photos-snippet
                                                          :inspection.pdf-template/photo-snippet]})

(def inspection-pdf-snippets
  [:inspection.pdf-template/table-for-visits-snippet
   :inspection.pdf-template/visit-snippet
   :inspection.pdf-template/inspector-snippet
   :inspection.pdf-template/table-for-deficiencies-snippet
   :inspection.pdf-template/deficiency-snippet
   :inspection.pdf-template/no-deficiencies-snippet
   :inspection.pdf-template/table-for-parties-snippet
   :inspection.pdf-template/party-snippet
   :inspection.pdf-template/no-parties-snippet])


(def inspection-pdf-templates
  (into #{:inspection.pdf-template/html-template}
        inspection-pdf-snippets))


(def pdf-template-editable-keys
  (into #{:inspection.pdf-template/name
          :inspection.pdf-template/type
          :inspection.pdf-template/print-css
          :inspection.pdf-template/active?}
        inspection-pdf-templates))


(def inspection-editable-keys
  #{:inspection/qualification
    :inspection/inspector-qualifications
    :inspection/descriptions
    :inspection/duration-minutes
    :inspection/due-date
    :inspection/priority
    :inspection/recurring?
    :inspection/recurrence-interval
    :inspection/triggers-refunds?})

(def concerned-roles #{:role/applicant :role/builder})


(def occupancy-inspection-visit-editable-keys
  [:inspection.visit/occupancy-granted-date
   :inspection.visit/occupancy-result
   :inspection.visit/occupancy-pdf-template
   :inspection.visit/occupancy-outstanding-matters
   :inspection.visit/occupancy-description
   :inspection.visit/partial-occupancy])


(def visit-editable-keys
  (into occupancy-inspection-visit-editable-keys
        [:inspection.visit/result
         :inspection.visit/visit-result
         :inspection.visit/type
         :inspection.visit/failure-reason
         :inspection.visit/result-details
         :inspection.visit/notes
         :inspection.visit/internal-notes
         :inspection.visit/time-spent
         :inspection.visit/general-report-template
         :inspection.visit/code-enforcement-report-template
         :inspection.visit/start-time]))

(def deficiency-editable-keys
  [:entity/created
   :inspection.deficiency/comply-by-date
   :inspection.deficiency/description
   :inspection.deficiency/remark?
   :inspection.deficiency/required-action
   :inspection.deficiency/reference
   :inspection.deficiency/comply-by-date-booking?
   :inspection.deficiency/photos])

(def violation-permit-types
  #{:permit-type/CE
    :permit-type/PW})

(def permit-types-without-deficiencies
  #{:permit-type/PWO})

(defn has-no-deficiencies? [permit-type]
  (contains? permit-types-without-deficiencies permit-type))

(def has-deficiencies? (complement has-no-deficiencies?))

(def order-editable-keys
  [:inspection.order/order-template
   :inspection.order/compliant-by-date
   :inspection.order/issued-to
   :inspection.order/deficiencies
   :inspection.order/additional-address-information
   :inspection.order/notes])

(def visit-type-editable-keys [:inspection-visit-type/names
                               :inspection-visit-type/workflow
                               :inspection-visit-type/visit-results])

(def visit-type-workflows [:inspection.visit.type-workflow/final-inspection
                           :inspection.visit.type-workflow/general-inspection
                           :inspection.visit.type-workflow/occupancy-inspection])

(def visit-result-editable-keys [:inspection-visit-result/names
                                 :inspection-visit-result/status-type
                                 :inspection-visit-result/rules])

(def visit-result-rules [:inspection.visit.result-rule/no-deficiencies-allowed
                         :inspection.visit.result-rule/close-inspection
                         :inspection.visit.result-rule/reason-required
                         :inspection.visit.result-rule/outstanding-bill-payment-required])

(def visit-result-status-types [:inspection.visit.result-status-type/in-progress
                                :inspection.visit.result-status-type/passed
                                :inspection.visit.result-status-type/failed
                                :inspection.visit.result-status-type/completed])

(def visit-results [;; not possible to perform inspection - no change in inspection result
                    :inspection.visit.result/no-access
                    ;; inspection result inconclusive
                    :inspection.visit.result/not-complete
                    ;; inspection failed
                    :inspection.visit.result/failed
                    ;; inspection passed
                    :inspection.visit.result/passed])


(def occupancy-visit-results [:inspection.visit.occupancy-result/occupancy-granted
                              :inspection.visit.occupancy-result/occupancy-granted-but-outstanding-matters
                              :inspection.visit.occupancy-result/occupancy-failed])

(def all-legacy-results
  (set/union (set visit-results) (set occupancy-visit-results)))

(defn visit-result-mapper [result]
  (case result
    :inspection.visit.occupancy-result/occupancy-granted :inspection.visit.result/passed
    :inspection.visit.occupancy-result/occupancy-granted-but-outstanding-matters :inspection.visit.result/not-complete
    :inspection.visit.occupancy-result/occupancy-failed :inspection.visit.result/failed
    nil))


(defn some-visit-passed? [visits]
  (or
    (->> visits
         (keep (comp :inspection-visit-result/status-type :inspection.visit/visit-result))
         (some (partial = :inspection.visit.result-status-type/passed)))
    (->> visits ;;TODO remove when :feature-flag.id/inspection-visit-configuration removed
         (keep :inspection.visit/result)
         (some (partial = :inspection.visit.result/passed)))))


(defn visit-result [visit]
  (:inspection.visit/visit-result visit))


(defn legacy-result-for-visit [visit]
  (or (:inspection.visit/occupancy-result visit)
      (:inspection.visit/result visit)))


(defn visit-type [visit]
  (-> visit :entity/parent :inspection/visit-type))


(defn visit-type-workflow-is? [workflow visit]
  (-> visit visit-type :inspection-visit-type/workflow (= workflow)))

(defn visit-result-workflows [visit-result]
  (->> visit-result
       :inspection-visit-type/_visit-results
       (map :inspection-visit-type/workflow)))

(defn visit-result-rule-set? [result rule]
  (-> result :inspection-visit-result/rules (contains? rule)))

(defn visit->inspection [visit]
  (:entity/parent visit))

(defn inspection->phase [inspection]
  (:entity/parent inspection))

(defn workspace [inspection]
  (-> inspection inspection->phase :entity/parent))

(defn visit-permit-type
  "Resolve the permit type for the workspace that owns the given visit."
  [visit]
  (some-> visit visit->inspection workspace :workspace/permit-type))

(defn visit-result-requires-bill-payment?
  "Check if a visit result has the outstanding bill payment rule set."
  [visit-result]
  (visit-result-rule-set? visit-result :inspection.visit.result-rule/outstanding-bill-payment-required))

(defn mandatory-visit-result-detail-missing? [visit]
  (and (-> visit visit-result (visit-result-rule-set? :inspection.visit.result-rule/reason-required))
       (-> visit :inspection.visit/result-details empty?)))

(defn visit-results-for-inspection [inspection]
  (->> inspection
       :inspection/visit-type
       :inspection-visit-type/visit-results))

(defn invalid-visit-result-for-type? [visit-type visit-result]
  (-> visit-type
      :inspection-visit-type/visit-results
      du/entity-id-set
      (contains? (get visit-result :db/id visit-result))
      not))

(def occupancy-granted-result-status-types
  #{:inspection.visit.result-status-type/passed
    :inspection.visit.result-status-type/in-progress})


(def occupancy-granted-results
  #{:inspection.visit.occupancy-result/occupancy-granted
    :inspection.visit.occupancy-result/occupancy-granted-but-outstanding-matters})


(defn occupancy-granted? [visit]
  (let [result     (or (visit-result visit)
                       (legacy-result-for-visit visit))
        visit-type (visit-type visit)]
    (or
      (and (some-> visit-type :inspection-visit-type/workflow (= :inspection.visit.type-workflow/occupancy-inspection))
           (some-> result :inspection-visit-result/status-type occupancy-granted-result-status-types))
      (contains? occupancy-granted-results result))))


(def visit-method [:inspection.visit.type/on-site
                   :inspection.visit.type/communication])


(def default-visit-method
  :inspection.visit.type/on-site)

(def base-failure-reasons [:inspection.visit.failure/info-required])

(def all-failure-reasons (concat base-failure-reasons
                                 [:inspection.visit.failure/deficiencies
                                  :inspection.visit.failure/violations
                                  :inspection.visit.failure/order-issued]))

(defn result-status-type-is? [status-type result]
  (-> result :inspection-visit-result/status-type (= status-type)))

(def failed-result?
  (partial result-status-type-is? :inspection.visit.result-status-type/failed))

(defn passed-or-completed-result?
  "Passed and completed should behave the same way."
  [result]
  (or (result-status-type-is? :inspection.visit.result-status-type/passed result)
      (result-status-type-is? :inspection.visit.result-status-type/completed result)))

(defn failure-reason [{:inspection.visit/keys [result failure-reason result-details]}]
  (when (and (or (failed-result? result)
                 (= result :inspection.visit.result/failed))
             (empty? result-details))
    (or failure-reason :inspection.visit.failure/info-required)))

(defn filter-applicable-reasons-fn
  "Filters out visit result details generated from hardcoded failure reasons if new visit configuration is not enabled."
  [show-failure-reasons?]
  (fn [result-details]
    (filter #(and (or show-failure-reasons?
                      (nil? (:visit-result-detail/key %)))
                  (-> %
                      :visit-result-detail/visit-result
                      du/is-active?))
            result-details)))

(def order-template-kinds
  [:order.template.kind/ON-uncover
   :order.template.kind/ON-tests-and-samples
   :order.template.kind/ON-subsection-12-2
   :order.template.kind/ON-subsection-15-9-4
   :order.template.kind/ON-emergency-comply
   :order.template.kind/ON-prohibiting-occupancy
   :order.template.kind/ON-stop-work
   :order.template.kind/order])

(def order-template-features
  [:order.template.feature/due-date
   :order.template.feature/notes
   :order.template.feature/linked-orders
   :order.template.feature/linked-deficiencies
   :order.template.feature/other-items])

(def checklist-result-sets
  [:inspection.checklist.result-set/yes-or-no
   :inspection.checklist.result-set/pass-or-fail
   :inspection.checklist.result-set/acceptable-or-not
   :inspection.checklist.result-set/approved-or-not])

(def checklist-item-result-values
  [:inspection.checklist-item.result-value/pass
   :inspection.checklist-item.result-value/fail
   :inspection.checklist-item.result-value/na])

(def checklist-result-set-entity-name-attributes
  [:inspection.checklist.result-set/names-pass
   :inspection.checklist.result-set/names-fail
   :inspection.checklist.result-set/names-na])

(def checklist-item-result->custom-result-set-attribute
  (->> (map hash-map checklist-item-result-values checklist-result-set-entity-name-attributes)
       (apply merge)))

(defn checklist-item-value->string [tr lang {:inspection.checklist/keys [custom-result-set result-set]} value]
  (if custom-result-set
    (i18n-model/get-text-from-entities-or-map
      (->> value
           checklist-item-result->custom-result-set-attribute
           (get custom-result-set))
      lang)
    (tr [(keyword (str "inspection.checklist.result-set."
                       (-> result-set name))
                  (name value))])))

(def is-inspection-template-kind?
  (partial eu/is-type? :entity.type/inspection-template-kind))

(def is-inspection-template?
  (partial eu/is-type? :entity.type/inspection-template))

(def is-visit?
  (partial eu/is-type? :entity.type/inspection-visit))

(def is-order-template?
  (partial eu/is-type? :entity.type/order-template))

(def is-inspection-checklist-template?
  (partial eu/is-type? :entity.type/inspection-checklist-template))

(defmethod du/is-active? :entity.type/inspection-template-kind
  [{state :domain/state}]
  (not= state :inspection.template.kind.state/retired))

(defmethod du/is-active? :entity.type/inspection-template
  [{state :domain/state}]
  (not= state :inspection.template.state/retired))

(defmethod du/is-active? :entity.type/inspection
  [{state :domain/state}]
  (not= state :inspection.state/draft))

(defmethod du/is-active? :entity.type/inspection-visit
  [{_ :domain/state}]
  true)

(defmethod du/is-active? :entity.type/inspection-deficiency
  [{state :domain/state}]
  (not= state :inspection.deficiency.state/removed))

(defmethod du/is-active? :entity.type/inspection-order
  [{state :domain/state}]
  (not= state :inspection.order.state/removed))

(defmethod du/is-active? :entity.type/order-template
  [{state :domain/state}]
  (not= state :order.template.state/retired))

(defmethod du/is-active? :entity.type/inspection-pdf-template
  [{active? :inspection.pdf-template/active?}]
  (true? active?))

(defmethod du/is-active? :entity.type/visit-type
  [{state :domain/state}]
  (= state :inspection-visit-type.state/active))

(defmethod du/is-active? :entity.type/visit-result
  [{state :domain/state}]
  (= state :inspection-visit-result.state/active))

(defmethod du/is-active? :entity.type/visit-result-detail
  [{state :domain/state}]
  (= state :visit-result-detail.state/active))

(defmethod du/is-active? :entity.type/inspector-qualification
  [qualification]
  (-> qualification :group/_inspector-qualification not-empty boolean))

(defmethod du/is-active? :entity.type/inspection-checklist-result-set
  [{state :domain/state}]
  (= state :inspection.checklist.result-set.state/active))

(defn general-inspection? [inspection]
  (-> inspection :inspection/visit-type :inspection-visit-type/workflow (= :inspection.visit.type-workflow/general-inspection)))

(defn final-inspection? [inspection]
  (-> inspection :inspection/visit-type :inspection-visit-type/workflow (= :inspection.visit.type-workflow/final-inspection)))

(defn visit->template-type
  "Resolve the `:inspection.pdf-template/type` of a visit"
  [visit]
  (cond
    (occupancy-granted? visit) :inspection.pdf-template.type/occupancy-permit
    (= :permit-type/CE (visit-permit-type visit)) :inspection.pdf-template.type/code-enforcement-report
    :else :inspection.pdf-template.type/general-report))

(def template-type->attrs
  {:inspection.pdf-template.type/occupancy-permit        {:template-attr :inspection.visit/occupancy-pdf-template
                                                          :file-attr     :inspection.visit/occupancy-permit-file}
   :inspection.pdf-template.type/code-enforcement-report {:template-attr :inspection.visit/code-enforcement-report-template
                                                          :file-attr     :inspection.visit/code-enforcement-report-file}
   :inspection.pdf-template.type/general-report          {:template-attr :inspection.visit/general-report-template
                                                          :file-attr     :inspection.visit/inspection-report-file}})

(defn visit->template-attributes
  [visit]
  (-> visit
      visit->template-type
      template-type->attrs))

(defn inspection-visit-file
  "Visit can either have occupancy permit file, code enforcement, or inspection report file"
  [visit]
  (or (:inspection.visit/occupancy-permit-file visit)
      (:inspection.visit/inspection-report-file visit)
      (:inspection.visit/code-enforcement-report-file visit)))


(defn pdf-template-for-inspection-visit [visit]
  (let [{:keys [template-attr]} (visit->template-attributes visit)
        template                (get visit template-attr)]
    (when (:inspection.pdf-template/active? template)
      template)))


(defn filter-inspection-templates-by-type
  [template-types templates]
  (->> templates
       (filter :inspection.pdf-template/active?)
       (filter #(->> % :inspection.pdf-template/type (contains? template-types)))))


(defn filter-inspection-templates
  [visit templates]
  (let [template-type (visit->template-type visit)]
    (filter-inspection-templates-by-type #{template-type} templates)))


(def deficiency-states-order
  "Deficiency states listed in sorting order, for
  `du/state-order-fn`"
  [:inspection.deficiency.state/draft
   #{:inspection.deficiency.state/active
     ;; cleared but visit is still ongoing
     :inspection.deficiency.state/tentatively-cleared}
   :inspection.deficiency.state/cleared
   :inspection.deficiency.state/removed])

(def ^{:arglists '([deficiency])} deficiency-sort-order
  "Produce the sort order tuple for a deficiency"
  (juxt (du/state-order-fn deficiency-states-order)
        :inspection.deficiency/remark?
        :entity/created))

(def deficiency-states
  (du/flatten-state-order-list deficiency-states-order))

(def quick-pick-deficiency-states
  [:inspection.quick-pick-deficiency.state/draft
   :inspection.quick-pick-deficiency.state/active
   :inspection.quick-pick-deficiency.state/removed])

(def deficiency-label-states
  [:inspection.deficiency-label.state/active
   :inspection.deficiency-label.state/cleared
   :inspection.deficiency-label.state/removed])

(def order-states
  [:inspection.order.state/draft
   :inspection.order.state/active
   :inspection.order.state/cleared
   :inspection.order.state/removed])

(def resolved-order-states
  #{:inspection.order.state/cleared
    :inspection.order.state/removed})

(defn resolved-order? [order]
  (contains? resolved-order-states (:domain/state order)))

(def acknowledgement-states
  #{:order.acknowledgement.state/pending
    :order.acknowledgement.state/accepted
    :order.acknowledgement.state/declined})


(def inspection-states [:inspection.state/draft
                        :inspection.state/active
                        :inspection.state/completed
                        :inspection.state/cancelled])


(def internal-inspection-states
  "Inspection states during which the inspection should only be
  visible internally to authority users"
  #{:inspection.state/draft})


(def internal-inspection-visit-states
  "Inspection visit states during which the inspection should
  only be visible internally to authority users"
  #{:inspection.visit.state/in-progress})


(def internal-inspection-order-states
  "Inspection order states during which the inspection should
  only be visible internally to authority users"
  #{:inspection.order.state/draft})


(def internal-inspection-deficiency-states
  "Inspection deficiency states during which the deficiency should
  only be visible internally to authority users"
  #{:inspection.deficiency.state/draft})

(defn invalid-attributes-for-visit-result-v2 [{:inspection-visit-type/keys [workflow]} {:inspection-visit-result/keys [status-type]}]
  (let [occupancy-attrs-and-failure-reason (conj occupancy-inspection-visit-editable-keys
                                                 :inspection.visit/failure-reason)]
    (case workflow
      :inspection.visit.type-workflow/occupancy-inspection
      (case status-type
        :inspection.visit.result-status-type/failed [:inspection.visit/occupancy-granted-date
                                                     :inspection.visit/occupancy-pdf-template
                                                     :inspection.visit/occupancy-outstanding-matters
                                                     :inspection.visit/occupancy-description]
        :inspection.visit.result-status-type/passed [:inspection.visit/occupancy-outstanding-matters
                                                     :inspection.visit/failure-reason]
        :inspection.visit.result-status-type/in-progress [:inspection.visit/failure-reason]
        nil)
      (case status-type
        :inspection.visit.result-status-type/failed occupancy-inspection-visit-editable-keys
        occupancy-attrs-and-failure-reason))))

(def inspection-template-kind-subtypes
  [:inspection.template.kind.subtype/general
   :inspection.template.kind.subtype/occupancy])

(def inspection-subtypes-by-template-kind-subtypes
  {:inspection.template.kind.subtype/general   :inspection.subtype/general
   :inspection.template.kind.subtype/occupancy :inspection.subtype/occupancy})

(def inspection-priorities
  [:inspection-priority/low
   :inspection-priority/normal
   :inspection-priority/high])

(def checklist-visibility-values
  [:inspection-checklist-visibility/hide
   :inspection-checklist-visibility/show-checklist-rows
   :inspection-checklist-visibility/show-checklist-rows-and-results])

(def order-pdf-snippets
  [:order.template/deficiency-html-snippet
   :order.template/condition-table-snippet
   :order.template/condition-snippet
   :order.template/order-html-snippet
   :order.template/other-html-snippet
   :order.template/checklist-table-snippet
   :order.template/checklist-snippet
   :order.template/checklist-group-snippet
   :order.template/checklist-item-snippet
   :order.template/party-html-snippet])

(def order-pdf-templates
  (into #{:order.template/html-template}
        order-pdf-snippets))

(def order-template-editable-keys
  (concat [:order.template/name
           :order.template/features
           :order-template/phases
           :order.template/print-css
           :order.template/html-template]
          order-pdf-snippets))

(defn checklists-from-order-deficiencies [deficiencies]
  (->> deficiencies
       (mapcat :inspection.checklist-item/_deficiency) ;checklist items
       (map :entity/parent) ;checklists
       distinct))

(def inspection-recurrence-options
  "What number months are available to select in the UI"
  (concat (range 1 13)
          [18 24 36]))

(def ^{:arglists '([inspection-template-kind])} inspection-templates
  "Get all child templates for an inspection template kind. Use `get-templates-for-kind` normally,
  it will filter out inactive templates."
  (child/child-entities-of-type-fn :entity.type/inspection-template))

(def get-templates-for-kind
  "Get active templates associated with an inspection kind"
  (comp (partial filter du/is-active?)
        inspection-templates))


(def has-templates?
  "Does the inspection kind have any associated templates?"
  (comp not empty? get-templates-for-kind))


(def verdict-states-for-editing #{:verdict.state/draft})

(def ^{:arglists '([inspection])} deficiencies
  (child/child-entities-of-type-fn :entity.type/inspection-deficiency))

(defn actual-deficiencies [inspection]
  (->> inspection
       deficiencies
       (remove (du/in-state-fn :inspection.deficiency.state/removed))))

(defn active-deficiencies [inspection]
  (->> inspection
       deficiencies
       (filter (du/in-state-fn :inspection.deficiency.state/active))))

(defn inspection-photos [inspection]
  (->> inspection
       file-model/file-children
       (filter #((set attachment-model/allowed-image-types) (:file/content-type %)))))

(defn authority-group [inspection]
  (-> inspection workspace :domain/authority-group))

(defn has-bookings-or-visits? [inspection]
  (boolean
    (or ((child/child-entity-of-type-fn :entity.type/booking) inspection)
        ((child/child-entity-of-type-fn :entity.type/inspection-visit) inspection))))

(def standard-workdays
  #{1 2 3 4 5})

(defn acknowledgement-accepted? [acknowledgement]
  (= (:domain/state acknowledgement) :order.acknowledgement.state/accepted))

(defn acknowledged-by-user? [acknowledgement]
  (let [ack-by (-> acknowledgement
                   :order.acknowledgement/acknowledged-by
                   :db/id)
        users  (->> acknowledgement
                    :order.acknowledgement/order
                    :inspection.order/issued-to
                    (map #(-> % :party/user :db/id)))]
    (and (not (nil? ack-by))
         (some #{ack-by} users))))


(defn validate-time-slot [start-hour start-minute end-hour end-minute]
  (or (< start-hour end-hour)
      (and (= start-hour end-hour)
           (< start-minute end-minute))))


(defn validate-time-slots [time-slots]
  ;; Check that start times are before end times
  (and (->> time-slots
            (every? (fn [{:time-slot/keys [start-hour start-minute end-hour end-minute]}]
                      (validate-time-slot start-hour start-minute end-hour end-minute))))
       ;; Check that every slot ends before the next starts
       (->> time-slots
            (sort-by (juxt :time-slot/start-hour :time-slot/start-minute))
            (partition 2 1)
            (every? (fn [[current-slot next-slot]]
                      (validate-time-slot (:time-slot/end-hour current-slot) (:time-slot/end-minute current-slot)
                                          (:time-slot/start-hour next-slot) (inc (:time-slot/start-minute next-slot))))))))


(def ^{:arglists '([inspection lang])} inspection-name
  "Get inspection name on a specific language"
  (i18n-model/translation-fallback-fn :inspection/names
                                      :inspection/name))

(def ^{:arglists '([inspection lang])} inspection-description
  "Get inspection description on a specific language"
  (i18n-model/translation-fallback-fn :inspection/descriptions
                                      :inspection/description))

(def ^{:arglists '([inspection-template-kind lang])} inspection-template-kind-name
  "Get inspection template kind name on a selected language"
  (i18n-model/translation-fallback-fn :inspection.template.kind/names
                                      :inspection.template.kind/name))

(def ^{:arglists '([inspection-template-kind lang])} inspection-template-kind-description
  "Get inspection template kind description on a selected language"
  (i18n-model/translation-fallback-fn :inspection.template.kind/descriptions
                                      :inspection.template.kind/description))

(def ^{:arglists '([order-template lang])} order-template-name
  "Get order template name on a selected language"
  (i18n-model/translation-fallback-fn :order.template/name))

(defn order-name [order lang]
  (-> order
      :inspection.order/order-template
      (order-template-name lang)))

(def ^{:arglists '([visit-result lang])} visit-type-name
  (i18n-model/translation-fallback-fn :inspection-visit-type/names))

(def ^{:arglists '([visit-result lang])} visit-result-name
  (i18n-model/translation-fallback-fn :inspection-visit-result/names))

(def ^{:arglists '([visit-result-detail lang])} visit-result-detail-name
  (i18n-model/translation-fallback-fn :visit-result-detail/names))

(def ^{:arglists '([visit-result-detail-label lang])} visit-result-detail-label-text
  (i18n-model/translation-fallback-fn :visit-result-detail-label/text))

(def ^{:arglists '([inspection-checklist-item lang])} inspection-checklist-item-descriptions
  (i18n-model/translation-fallback-fn :inspection.checklist-item/descriptions))

(def ^{:arglists '([inspection-checklist-group lang])} inspection-checklist-group-name
  (i18n-model/translation-fallback-fn :inspection.checklist-item.group/names))

(def ^{:arglists '([inspection-checklist lang])} inspection-checklist-name
  (i18n-model/translation-fallback-fn :inspection.checklist/names))

(def ^{:arglists '([inspector-qualification lang])} inspector-qualification-name
  (i18n-model/translation-fallback-fn :inspector-qualification/names))

(defmethod group-model/get-authority-group :entity.type/inspection
  [inspection]
  (-> inspection :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-deficiency
  [deficiency]
  (-> deficiency :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-template-kind
  [inspection-template-kind]
  (:domain/authority-group inspection-template-kind))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist-template
  [inspection-checklist-template]
  (:domain/authority-group inspection-checklist-template))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist-item-group-template
  [inspection-checklist-item-group-template]
  (-> inspection-checklist-item-group-template :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist-item-template
  [inspection-checklist-item-template]
  (-> inspection-checklist-item-template :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist
  [inspection-checklist]
  (-> inspection-checklist :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist-item-group
  [inspection-checklist-item-group]
  (-> inspection-checklist-item-group :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist-item
  [inspection-checklist-item]
  (-> inspection-checklist-item :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/inspection-checklist-item-result
  [inspection-checklist-item-result]
  (-> inspection-checklist-item-result :entity/parent group-model/get-authority-group))

(defmethod group-model/get-authority-group :entity.type/visit-type
  [visit-type]
  (:domain/authority-group visit-type))

(defmethod group-model/get-authority-group :entity.type/visit-result
  [result]
  (:domain/authority-group result))

(defmethod group-model/get-authority-group :entity.type/visit-result-detail
  [result-detail]
  (:domain/authority-group result-detail))

(defmethod group-model/get-authority-group :entity.type/visit-result-detail-label
  [result-detail-label]
  (:visit-result-detail-label/group result-detail-label))

(defmethod group-model/get-authority-group :entity.type/inspection-visit
  [visit]
  (-> visit :entity/parent group-model/get-authority-group))

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection [_]
  [:inspection/names
   :inspection/descriptions])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-template-kind [_]
  [:inspection.template.kind/names
   :inspection.template.kind/descriptions])

(defmethod i18n-model/get-localized-string-attrs :entity.type/visit-type [_]
  [:inspection-visit-type/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/visit-result [_]
  [:inspection-visit-result/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/visit-result-detail [_]
  [:visit-result-detail/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/visit-result-detail-label [_]
  [:visit-result-detail-label/text])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist-template [_]
  [:inspection.checklist/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist-item-group-template [_]
  [:inspection.checklist-item.group/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist-item-template [_]
  [:inspection.checklist-item/descriptions])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist [_]
  [:inspection.checklist/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist-item-group [_]
  [:inspection.checklist-item.group/names])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist-item [_]
  [:inspection.checklist-item/descriptions])

(defmethod i18n-model/get-localized-string-attrs :entity.type/inspection-checklist-result-set [_]
  checklist-result-set-entity-name-attributes)

(def ^{:arglists '([phase-target])} phase-inspections
  (child/child-entities-of-type-fn :entity.type/inspection))

(def ^{:arglists '([phase-target])} phase-orders
  (child/child-entities-of-type-fn :entity.type/inspection-order))

(defn all-phase-orders-resolved? [phase-target]
  (every? resolved-order? (phase-orders phase-target)))

(def ^{:arglists '([inspection])} inspection-visits
  (child/child-entities-of-type-fn :entity.type/inspection-visit))

(defn inspection-active-visit [inspection]
  (->> inspection
       inspection-visits
       (filter (du/in-state-fn :inspection.visit.state/in-progress))
       first))

(defn no-other-open-final-inspections? [visit]
  (let [this-inspection-id (-> visit visit->inspection :db/id)]
    (->> visit
         visit->inspection
         inspection->phase
         phase-inspections
         (filter final-inspection?)
         (remove #(= (:db/id %) this-inspection-id))
         (remove (du/in-state-fn :inspection.state/cancelled))
         (remove (du/in-state-fn :inspection.state/completed))
         empty?)))

(defn visit-result-will-close-workspace? [visit]
  (and (visit-type-workflow-is? :inspection.visit.type-workflow/final-inspection visit)
       (visit-result-rule-set? (:inspection.visit/visit-result visit) :inspection.visit.result-rule/close-inspection)
       (no-other-open-final-inspections? visit)))

(defn inspection-visit-end-signal-id-candidates [visit]
  (if (visit-result-will-close-workspace? visit)
    [:process.signal/end-inspection-visit-and-close-code-enforcement
     :process.signal/end-inspection-visit-and-ready-to-issue
     :process.signal/end-inspection-visit-and-finish
     :process.signal/end-inspection-visit]
    [:process.signal/end-inspection-visit]))

(def ^{:arglists '([inspection])} inspection-bookings
  (child/child-entities-of-type-fn :entity.type/booking))

(def ^{:arglists '([checklist-template])} checklist-item-group-templates
  (child/child-entities-of-type-fn :entity.type/inspection-checklist-item-group-template))

(def ^{:arglists '([checklist-template])} checklist-item-templates
  (child/child-entities-of-type-fn :entity.type/inspection-checklist-item-template))

(def ^{:arglists '([inspection-template-kind])} checklist-templates
  (child/child-entities-of-type-fn :entity.type/inspection-checklist-template))

(def ^{:arglists '([checklist])} checklists
  (child/child-entities-of-type-fn :entity.type/inspection-checklist))

(def ^{:arglists '([checklist])} checklist-groups
  (child/child-entities-of-type-fn :entity.type/inspection-checklist-item-group))

(def ^{:arglists '([checklist])} checklist-items
  (child/child-entities-of-type-fn :entity.type/inspection-checklist-item))

(def ^{:arglists '([group])} checklist-group-items
  :inspection.checklist-item/_group)

(def ^{:arglists '([checklist-item])} checklist-item-result
  (comp first
        (child/child-entities-of-type-fn :entity.type/inspection-checklist-item-result)))

(def ^{:arglists '([order])} order-files
  (juxt :inspection.order/signed-order
        :inspection.order/order-document))

(def checklist-item->result-value
  (comp :inspection.checklist-item.result/value checklist-item-result))

(defn checklist-items-of-result-value [value checklist]
  (->> checklist
       checklist-items
       (filter #(-> %
                    checklist-item->result-value
                    (= value)))))

(defn checklist-has-result-value? [value checklist]
  (->> checklist
       (checklist-items-of-result-value value)
       seq
       boolean))

(defn has-checklists-v2? [group-or-features feature-flags]
  (group-model/has-mobile-feature?
    [(-> feature-flags :feature-flag.id/checklist-configuration-v2)
     (-> feature-flags :feature-flag.id/checklist-configuration-v2-mobile)
     (if (sequential? group-or-features)
       (contains? group-or-features :group.feature/mobile-app)
       (group-model/has-feature? group-or-features :group.feature/mobile-app))]))

(defn enforcement-report-has-checklists?
  "Checklist snippet is shown in the enforcement inspection report only when v2 checklists
   are available and the WIN-5715 feature flag is enabled."
  [group-or-features feature-flags]
  (and (has-checklists-v2? group-or-features feature-flags)
       (boolean (:feature-flag.id/enforcement-inspection-report-checklists feature-flags))))

(def ^{:arglists '([authority-group])} order-templates-from-group
  (group-model/entities-of-group-fn :entity.type/order-template))

(defn deficiency-linked-orders [deficiency]
  (->> deficiency
       :inspection.order/_deficiencies
       (filter #(not= (:domain/state %) :inspection.order.state/removed))))

(def inspection-visit-pdf-template-types
  #{:inspection.visit/occupancy-pdf-template
    :inspection.visit/general-report-template
    :inspection.visit/code-enforcement-report-template})

(defn quick-pass-applicable?
  "Only general inspections have support for quick pass inspection. If multiple inspection
   times are booked and open, quick pass is not allowed."
  [inspection]
  (and (general-inspection? inspection)
       (-> inspection
           authority-group
           :group/features
           set
           (contains? :group.feature/inspection-quick-pass))
       (empty? (active-deficiencies inspection))
       (->> inspection
            inspection-bookings
            (filter (du/in-state-fn :booking.state/to-dispatch :booking.state/dispatched))
            count
            (= 1))))

(defn quick-pass-applicable-result?
  "Only results used in general inspections are available for quick pass."
  [visit-result]
  (->> visit-result
       visit-result-workflows
       (some #{:inspection.visit.type-workflow/general-inspection})
       boolean))

(defn get-inspector-qualifications
  [inspector authority-group-ids]
  (->> inspector
       :user/groups
       (remove nil?)
       (group-by (fn [group]
                   (-> group
                       :entity/parent ;inspector group
                       :entity/parent ;authority group
                       :db/id)))
       (filter #(contains? authority-group-ids (key %)))
       (map (fn [[k v]]
              [k (->> v
                      (map :group/inspector-qualification)
                      (remove nil?))]))
       (into {})))

(defn no-inspection-qualification-restriction?
  "If qualification restriction is enabled user must fulfill one of the following:
   - manager role
   - booking assignee (dispatched to)
   - have all the qualifications required by inspection
   - super admin"
  [{:keys [user target inspection booking-id]}]
  (let [required-qualifications (->> inspection :inspection/inspector-qualifications du/entity-id-set)]
    (or (empty? required-qualifications)
        (let [booking                      (du/find-descendant {:db/id booking-id} inspection)
              auth-group                   (group-model/get-authority-group target)
              inspector-qualifications-set (-> auth-group
                                               (group-model/group-qualifications-for-user user)
                                               du/entity-id-set)]
          (or (-> auth-group
                  (group-model/has-feature? :group.feature/inspection-restriction-qualification)
                  not)
              (group-model/any-required-authority-role? auth-group user #{:group.role/manager})
              (= (-> booking
                     :booking/assignee
                     :db/id)
                 (-> user
                     :db/id))
              (set/subset? required-qualifications
                           inspector-qualifications-set)
              (guard-utils/user-is-admin? user))))))

(defn inspections-by-template-kind
  "Find all inspections that match the dependency's required template kind."
  [inspections required-kind]
  (filter #(= (:db/id required-kind)
              (-> % :inspection/template :entity/parent :db/id))
          inspections))

(defn workspace-dependencies
  "Filter dependencies to only include those where the required inspection exists in the workspace."
  [all-inspections dependencies]
  (let [workspace-template-kinds (->> all-inspections
                                      (remove (du/in-state-fn :inspection.state/cancelled))
                                      (map #(-> % :inspection/template :entity/parent :db/id))
                                      set)]
    (filter (fn [dep]
              (contains? workspace-template-kinds
                         (-> dep :inspection-dependency/required-template-kind :db/id)))
            dependencies)))

(defn get-latest-visit-result
  "Get the latest visit result entity for an inspection."
  [inspection]
  (when inspection
    (let [visit-with-result (->> (inspection-visits inspection)
                                 (remove (du/in-state-fn :inspection.visit.state/in-progress))
                                 (sort-by :inspection.visit/start-time)
                                 last)]
      (some-> visit-with-result visit-result))))

(defn dependency-satisfied?
  [dep inspections]
  (let [required-kind    (:inspection-dependency/required-template-kind dep)
        required-results (:inspection-dependency/required-visit-results dep)
        inspections      (inspections-by-template-kind inspections required-kind)]
    (some (fn [inspection]
            (when-let [visit-result (get-latest-visit-result inspection)]
              (some #(= (:db/id visit-result) (:db/id %)) required-results)))
          inspections)))

(defn dependencies-satisfied?
  [dependencies inspections]
  (if (empty? dependencies)
    true
    (every? #(dependency-satisfied? % inspections) dependencies)))

(defn is-final-inspection-dependency?
  [dep]
  (-> dep
      :inspection-dependency/required-template-kind
      :inspection.template.kind/visit-type
      :inspection-visit-type/workflow
      (= :inspection.visit.type-workflow/final-inspection)))

(defn workspace-inspection-dependencies
  "Get the workspace-applicable dependencies for an inspection (non-final, workspace-filtered)."
  [inspection inspections]
  (->> (-> inspection :inspection/template :entity/parent :inspection.template.kind/dependencies)
       (remove is-final-inspection-dependency?)
       (workspace-dependencies inspections)))

(defn can-request-inspection?
  [inspection inspections authority-user?]
  (let [dependencies (workspace-inspection-dependencies inspection inspections)]
    (or authority-user?
        (some? (get-latest-visit-result inspection))
        (empty? dependencies)
        (dependencies-satisfied? dependencies inspections))))

(defn inspection-template-kind [inspection]
  (-> inspection :inspection/template :entity/parent))

(defn inspection-visible-to-applicant? [permit-type inspection]
  (and (not (ptc-model/permit-types-without-applicant-visible-inspections permit-type))
       (template-kind-visible-to-applicant? (inspection-template-kind inspection))))

(def ^{:arglists '([inspection-template-kind])} template-kind-quick-pick-deficiencies
  (child/child-entities-of-type-fn :entity.type/inspection-quick-pick-deficiency))

(defn active-quick-pick-deficiencies
  "Gets active qpds, sorted by weight descending and reference ascending."
  [template-kind leaf-config-id]
  (when template-kind
    (->> #?(:clj (let [db (d/entity-db template-kind)]
                   (->> (d/q '[:find [?e ...]
                               :in $ ?type-tuple ?config
                               :where
                               [?e :domain/state :inspection.quick-pick-deficiency.state/active]
                               [?e :entity/parent+type ?type-tuple]
                               [?e :inspection.quick-pick-deficiency/group-config ?config]]
                             db
                             [(:db/id template-kind) :entity.type/inspection-quick-pick-deficiency]
                             leaf-config-id)
                        (map #(d/entity db %))))
            :cljs (->> (template-kind-quick-pick-deficiencies template-kind)
                       (filter (fn [{:inspection.quick-pick-deficiency/keys [group-config]
                                     :domain/keys                           [state]}]
                                 (and (= :inspection.quick-pick-deficiency.state/active state)
                                      (= (:db/id group-config) leaf-config-id))))))
         (sort-by (juxt (comp - :inspection.quick-pick-deficiency/weight)
                        :inspection.quick-pick-deficiency/reference)))))

(def draft-quick-pick-deficiencies-fetch-limit 10)

(defn draft-quick-pick-deficiencies
  "In clj: Gets draft qpds, 10 at a time. Returns `:has-more?` boolean to indicate if there are more to fetch.
   In cljs: Gets all draft qpds in entity-db for the given params.
   Returns qpds sorted by created date descending."
  #?(:clj ([template-kind leaf-config-id from-date to-date offset]
           (let [db    (d/entity-db template-kind)
                 all   (->> (d/q '[:find [?e ...]
                                   :in $ ?type-tuple ?config ?from-date ?to-date
                                   :where
                                   [?e :entity/parent+type ?type-tuple]
                                   [?e :entity/created ?created]
                                   [(<= ?from-date ?created)]
                                   [(<= ?created ?to-date)]
                                   [?e :inspection.quick-pick-deficiency/group-config ?config]
                                   [?e :domain/state :inspection.quick-pick-deficiency.state/draft]]
                                 db
                                 [(:db/id template-kind) :entity.type/inspection-quick-pick-deficiency]
                                 leaf-config-id
                                 from-date
                                 to-date)
                            (map #(d/entity db %))
                            (sort-by :entity/created #(compare %2 %1)))
                 items (->> all (drop offset) (take draft-quick-pick-deficiencies-fetch-limit))]
             {:items     items
              :has-more? (> (count all) (+ offset (count items)))}))
     :cljs ([template-kind leaf-config-id from-date to-date]
            (->> (template-kind-quick-pick-deficiencies template-kind)
                 (filter (fn [{:inspection.quick-pick-deficiency/keys [group-config]
                               :domain/keys                           [state]
                               :entity/keys                           [created]}]
                           (and (<= from-date created to-date)
                                (= (:db/id group-config) leaf-config-id)
                                (= :inspection.quick-pick-deficiency.state/draft state))))
                 (sort-by :entity/created #(compare %2 %1))))))

(defn active-quick-pick-deficiency? [quick-pick-deficiency inspection ws-leaf-config]
  (let [template-kind (-> inspection :inspection/template :entity/parent)]
    (and (du/in-state? :inspection.quick-pick-deficiency.state/active quick-pick-deficiency)
         (= template-kind (:entity/parent quick-pick-deficiency))
         (= ws-leaf-config (:inspection.quick-pick-deficiency/group-config quick-pick-deficiency)))))

(defn inspection-ai-enabled?
  "Is the inspection AI assistant enabled for the given authority group?
   Checks that the group is a Canadian building permit group with inspections enabled,
   and that the appropriate global feature flag is on (main flag for non-Quebec,
   Quebec flag for Quebec groups)."
  [auth-group ai-flag-enabled? ai-quebec-flag-enabled?]
  (let [country         (->> (group-model/scope-municipalities auth-group)
                             (some :municipality/country))
        building-group? (-> auth-group
                            group-model/get-permit-types
                            (contains? :permit-type/B))
        in-quebec?      (group-model/authority-group-in-subdivision? :CA-QC auth-group)]
    (and (group-model/has-feature? auth-group :group.feature/inspections)
         (= :CA country)
         building-group?
         (or (and ai-flag-enabled? (not in-quebec?))
             (and ai-quebec-flag-enabled? in-quebec?)))))
